import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Upload, Camera, FileText, Trash2, CheckCircle2, Loader2,
  ExternalLink, Filter, Inbox, X, Sparkles, AlertTriangle, Files,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import ScanPreviewDialog from "../components/ScanPreviewDialog";
import SuppliersDashboard from "../components/SuppliersDashboard";
import PossibleDuplicatesPanel from "../components/PossibleDuplicatesPanel";
import TaxesEditor from "../components/TaxesEditor";

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf";

/** Loads a File/Blob into an HTMLImageElement. */
function loadImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

const PGC_OPTIONS = [
  { v: "600", l: "(600) Compres mercaderies" },
  { v: "601", l: "(601) Compres matèries primeres" },
  { v: "602", l: "(602) Compres altres aprovisionaments" },
  { v: "621", l: "(621) Arrendaments i cànons" },
  { v: "622", l: "(622) Reparacions i conservació" },
  { v: "623", l: "(623) Serveis professionals" },
  { v: "624", l: "(624) Transports" },
  { v: "625", l: "(625) Primes d'assegurances" },
  { v: "626", l: "(626) Serveis bancaris" },
  { v: "627", l: "(627) Publicitat i RRPP" },
  { v: "628", l: "(628) Subministraments (llum, aigua, telèfon)" },
  { v: "629", l: "(629) Altres serveis" },
  { v: "631", l: "(631) Altres tributs" },
];

const CATEGORIES = [
  "Aprovisionaments", "Subministraments", "Lloguer", "Telecomunicacions",
  "Publicitat i màrqueting", "Manteniment", "Material d'oficina", "Combustible",
  "Restaurants i hostalatge", "Software i subscripcions", "Assegurances",
  "Bancs i comissions", "Transport", "Altres serveis",
];

export default function ReceivedInvoices() {
  const { t, lang } = useI18n();
  const [view, setView] = useState("invoices"); // "invoices" | "suppliers"
  const [supplierFilter, setSupplierFilter] = useState(null); // {nif, name} | null
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Multi-page scan: array of image Files. null = dialog closed; [] briefly during camera pick.
  const [scanPages, setScanPages] = useState(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null); // {pages?, file?, mode, existing}
  const [bulkQueue, setBulkQueue] = useState(null); // File[] selected for bulk upload
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const bulkRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (supplierFilter?.nif) params.supplier_nif = supplierFilter.nif;
      else if (supplierFilter?.name) params.supplier = supplierFilter.name;
      const [listRes, sumRes] = await Promise.all([
        api.get("/received-invoices", { params }),
        api.get("/received-invoices/summary"),
      ]);
      setList(listRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, supplierFilter]); // eslint-disable-line

  // Centralized error handler — surfaces plan-limit-reached (402) as a
  // dedicated toast with an "Upgrade" CTA.
  const handleApiError = (e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 402 && detail?.code === "plan_limit_reached") {
      toast.error(
        lang === "es"
          ? `Límite del plan alcanzado (${detail.used}/${detail.limit})`
          : `Límit del pla assolit (${detail.used}/${detail.limit})`,
        {
          description: lang === "es"
            ? "Actualiza a Pro para acceso ilimitado."
            : "Actualitza a Pro per accés il·limitat.",
          duration: 8000,
          action: {
            label: lang === "es" ? "Ver planes" : "Veure plans",
            onClick: () => window.location.href = "/app/pricing",
          },
        }
      );
      return true;
    }
    return false;
  };

  // Drill-down from the Suppliers tab: switch to Factures + apply NIF/name filter
  const drillDownSupplier = (row) => {
    setSupplierFilter({
      nif: row.supplier_nif || "",
      name: row.supplier_name || "",
    });
    setStatusFilter("all");
    setView("invoices");
  };

  // Show a rich success toast with the extracted summary + a "View" action
  // that re-opens the editor in case the user dismissed it.
  const showInvoiceSuccessToast = (data, pageCount = 1) => {
    const supplier = data.supplier_name?.trim() || (lang === "es" ? "Proveedor pendiente" : "Proveïdor pendent");
    const total = Number(data.total) || 0;
    const fmt = new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
      .format(total);
    const parts = [supplier];
    if (total > 0) parts.push(fmt);
    if (pageCount > 1) {
      parts.push(lang === "es" ? `${pageCount} págs.` : `${pageCount} pàgs.`);
    }
    toast.success(
      lang === "es" ? "Factura procesada" : "Factura processada",
      {
        description: parts.join(" · "),
        duration: 6000,
        action: {
          label: lang === "es" ? "Ver" : "Veure",
          onClick: () => setEditing(data),
        },
      }
    );
    // Anti-fraud heuristic: alert when the OCR'd total is suspiciously
    // higher than the supplier's historical pattern.
    if (data.ocr_anomaly) {
      toast.warning(
        lang === "es" ? "Importe sospechoso — revisa antes de validar" : "Import sospitós — revisa abans de validar",
        {
          description: data.ocr_anomaly_reason || "",
          duration: 9000,
          action: {
            label: lang === "es" ? "Revisar" : "Revisar",
            onClick: () => setEditing(data),
          },
        }
      );
    }
    // Per-product VAT memory auto-correction: surface what was changed so the
    // user can spot-check (or undo via the supplier VAT memory endpoint).
    const corrections = data.vat_memory_corrections || [];
    if (corrections.length > 0) {
      const sample = corrections.slice(0, 3)
        .map((c) => `${c.sample_desc || c.product_key}: ${c.old_rate}% → ${c.new_rate}%`)
        .join(" · ");
      const more = corrections.length > 3 ? ` (+${corrections.length - 3})` : "";
      toast.info(
        lang === "es"
          ? `IVA corregido por memoria del proveedor (${corrections.length} ítem)`
          : `IVA corregit per memòria del proveïdor (${corrections.length} ítem)`,
        { description: sample + more, duration: 7000 }
      );
    }
  };

  // Gallery / file picker (single file) → opens preview as 1-page scan
  const onPick = async (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error(lang === "es" ? "Máximo 10 MB" : "Màxim 10 MB");
      return;
    }
    // PDFs are digital — go straight to OCR.
    if (file.type === "application/pdf") {
      await runUpload(file, "raw");
      return;
    }
    setScanPages([file]);
  };

  // Camera capture — either starts a new scan or appends a page to the current one
  const onCameraCapture = (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error(lang === "es" ? "Máximo 10 MB por página" : "Màxim 10 MB per pàgina");
      return;
    }
    if (file.type === "application/pdf") {
      // shouldn't happen with capture=environment but just in case
      runUpload(file, "raw");
      return;
    }
    setScanPages((cur) => (cur && cur.length > 0 ? [...cur, file] : [file]));
  };

  // Trigger the OS camera again to add another page to the current scan
  const addPage = () => {
    if (cameraRef.current) {
      cameraRef.current.value = "";
      cameraRef.current.click();
    }
  };

  // Remove one page from the staged scan; close dialog if last page removed
  const removePage = (idx) => {
    setScanPages((cur) => {
      if (!cur) return cur;
      const next = cur.filter((_, i) => i !== idx);
      return next.length === 0 ? null : next;
    });
  };

  // Reorder pages via drag-and-drop (from → to)
  const reorderPages = (from, to) => {
    setScanPages((cur) => {
      if (!cur || from === to || from < 0 || to < 0 ||
          from >= cur.length || to >= cur.length) return cur;
      const next = [...cur];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  // Rotate a single page 90° CW. Loads the File into an Image, draws to a
  // canvas with swapped dimensions, exports as a new File and replaces the
  // entry in scanPages at the same index.
  const rotatePage = async (idx) => {
    const cur = scanPages;
    if (!cur || idx < 0 || idx >= cur.length) return;
    const orig = cur[idx];
    try {
      const img = await loadImageFile(orig);
      const canvas = document.createElement("canvas");
      canvas.width = img.height;   // swap
      canvas.height = img.width;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);     // 90° CW
      ctx.drawImage(img, 0, 0);
      const outType = orig.type === "image/png" ? "image/png" : "image/jpeg";
      const quality = outType === "image/jpeg" ? 0.92 : undefined;
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error("toBlob failed")), outType, quality);
      });
      const newName = orig.name.replace(/(\.[^.]+)?$/, (ext) => `-rot${ext || ""}`);
      const rotated = new File([blob], newName, { type: outType, lastModified: Date.now() });
      setScanPages((cur2) => {
        if (!cur2) return cur2;
        const next = [...cur2];
        next[idx] = rotated;
        return next;
      });
    } catch (e) {
      toast.error(lang === "es" ? "No se pudo rotar la página" : "No s'ha pogut girar la pàgina");
    }
  };

  // Process the staged scan: 1 page → /upload; ≥2 → /upload-multipage
  const runScanUpload = async (mode, force = false, pagesOverride = null) => {
    const pages = pagesOverride || scanPages;
    if (!pages || pages.length === 0) return;
    if (pages.length === 1) {
      await runUpload(pages[0], mode, force);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      pages.forEach((f) => fd.append("files", f));
      fd.append("mode", mode || "auto");
      const url = force
        ? "/received-invoices/upload-multipage?force=true"
        : "/received-invoices/upload-multipage";
      const r = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const data = r.data;
      showInvoiceSuccessToast(data, pages.length);
      if (data.ocr_engine === "tesseract") {
        toast.info(
          lang === "es"
            ? "OCR local activado — revisa los datos extraídos"
            : "OCR local activat — revisa les dades extretes",
          { duration: 5000 }
        );
      }
      if (data.duplicate_warning) {
        toast.warning(
          lang === "es"
            ? `Posible duplicado: ${data.duplicate_warning.document_number}`
            : `Possible duplicat: ${data.duplicate_warning.document_number}`,
          { duration: 6000 }
        );
      }
      setEditing(data);
      setScanPages(null);
      setDuplicatePrompt(null);
      load();
    } catch (e) {
      if (handleApiError(e)) return;
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "duplicate_file") {
        setDuplicatePrompt({ pages: [...pages], mode, existing: detail.existing });
      } else if (e?.response?.status === 409 && detail?.code === "duplicate_invoice") {
        setDuplicatePrompt({ pages: [...pages], mode, existing: detail.existing,
                             reason: "invoice" });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Error"));
      }
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const runUpload = async (file, mode, force = false) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode || "auto");
      const url = force ? "/received-invoices/upload?force=true" : "/received-invoices/upload";
      const r = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const data = r.data;
      showInvoiceSuccessToast(data, 1);
      if (data.ocr_engine === "tesseract") {
        toast.info(
          lang === "es"
            ? "OCR local activado — revisa los datos extraídos"
            : "OCR local activat — revisa les dades extretes",
          { duration: 5000 }
        );
      }
      if (data.duplicate_warning) {
        toast.warning(
          lang === "es"
            ? `Posible duplicado: ${data.duplicate_warning.document_number}`
            : `Possible duplicat: ${data.duplicate_warning.document_number}`,
          { duration: 6000 }
        );
      }
      setEditing(data);
      setScanPages(null);
      setDuplicatePrompt(null);
      load();
    } catch (e) {
      if (handleApiError(e)) return;
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "duplicate_file") {
        // Show the duplicate dialog instead of an error toast
        setDuplicatePrompt({ file, mode, existing: detail.existing });
      } else if (e?.response?.status === 409 && detail?.code === "duplicate_invoice") {
        setDuplicatePrompt({ file, mode, existing: detail.existing,
                             reason: "invoice" });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Error"));
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleDelete = async (id, supplier) => {
    if (!window.confirm(lang === "es"
      ? `¿Eliminar la factura de ${supplier}?`
      : `Eliminar la factura de ${supplier}?`)) return;
    try {
      await api.delete(`/received-invoices/${id}`);
      toast.success(lang === "es" ? "Eliminada" : "Eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const fmt = (n) => new Intl.NumberFormat("ca-ES",
    { style: "currency", currency: "EUR" }).format(Number(n) || 0);

  const totalMonth = summary?.monthly?.reduce((s, m) => s + m.total, 0) || 0;
  const vatMonth = summary?.monthly?.reduce((s, m) => s + m.vat, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {lang === "es" ? "Facturas recibidas" : "Factures rebudes"}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {lang === "es"
              ? "Escanea, archiva y clasifica tus facturas de proveedores"
              : "Escaneja, arxiva i classifica les teves factures de proveïdors"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                 className="hidden" onChange={(e) => onCameraCapture(e.target.files?.[0])}
                 data-testid="received-camera-input" />
          <input ref={inputRef} type="file" accept={ACCEPTED}
                 className="hidden" onChange={(e) => onPick(e.target.files?.[0])}
                 data-testid="received-file-input" />
          <input ref={bulkRef} type="file" accept={ACCEPTED} multiple
                 className="hidden"
                 onChange={(e) => {
                   const files = Array.from(e.target.files || []);
                   if (files.length) setBulkQueue(files);
                 }}
                 data-testid="received-bulk-input" />
          <Button
            variant="outline"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading || !!bulkQueue}
            className="border-white/10"
            data-testid="received-camera-btn"
          >
            <Camera size={16} className="mr-2" />
            {lang === "es" ? "Foto" : "Foto"}
          </Button>
          <Button
            variant="outline"
            onClick={() => bulkRef.current?.click()}
            disabled={uploading || !!bulkQueue}
            className="border-white/10 hidden sm:inline-flex"
            data-testid="received-bulk-btn"
            title={lang === "es" ? "Sube varios ficheros a la vez" : "Puja diversos fitxers de cop"}
          >
            <Files size={16} className="mr-2" />
            {lang === "es" ? "Lote" : "Lot"}
          </Button>
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !!bulkQueue}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="received-upload-btn"
          >
            {uploading ? (
              <><Loader2 size={16} className="mr-2 animate-spin" />
              {lang === "es" ? "Procesando..." : "Processant..."}</>
            ) : (
              <><Upload size={16} className="mr-2" />
              {lang === "es" ? "Subir" : "Pujar"}</>
            )}
          </Button>
        </div>
      </div>

      {/* View tabs: Invoices ↔ Suppliers (FASE 6) */}
      <div className="flex items-center gap-1 border-b border-white/5 -mt-2">
        <button
          type="button"
          onClick={() => setView("invoices")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
            view === "invoices"
              ? "border-blue-500 text-white"
              : "border-transparent text-slate-400 hover:text-slate-200"
          }`}
          data-testid="received-tab-invoices"
        >
          <Inbox size={14} />
          {lang === "es" ? "Facturas" : "Factures"}
        </button>
        <button
          type="button"
          onClick={() => setView("suppliers")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
            view === "suppliers"
              ? "border-blue-500 text-white"
              : "border-transparent text-slate-400 hover:text-slate-200"
          }`}
          data-testid="received-tab-suppliers"
        >
          <Users size={14} />
          {lang === "es" ? "Proveedores" : "Proveïdors"}
        </button>
      </div>

      {view === "suppliers" ? (
        <SuppliersDashboard onDrillDown={drillDownSupplier} />
      ) : (
        <>
      {/* Possible duplicates panel — only renders when groups exist */}
      <PossibleDuplicatesPanel onChange={load} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label={lang === "es" ? "Facturas año" : "Factures any"}
                 value={summary?.monthly?.reduce((s, m) => s + m.count, 0) || 0} />
        <KpiCard label={lang === "es" ? "Total año" : "Total any"} value={fmt(totalMonth)} />
        <KpiCard label={lang === "es" ? "IVA soportado" : "IVA suportat"} value={fmt(vatMonth)} accent="emerald" />
        <KpiCard label={lang === "es" ? "Pendientes" : "Pendents"}
                 value={list.filter((r) => r.status === "pending").length} accent="amber" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-slate-400" />
        {["all", "pending", "validated", "paid"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              statusFilter === s
                ? "bg-blue-600 border-blue-500 text-white"
                : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
            }`}
            data-testid={`received-filter-${s}`}
          >
            {s === "all" ? (lang === "es" ? "Todas" : "Totes")
              : s === "pending" ? (lang === "es" ? "Pendientes" : "Pendents")
              : s === "validated" ? (lang === "es" ? "Validadas" : "Validades")
              : (lang === "es" ? "Pagadas" : "Pagades")}
          </button>
        ))}
        {supplierFilter && (
          <span
            className="ml-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border bg-blue-500/15 border-blue-500/40 text-blue-100"
            data-testid="received-supplier-filter-chip"
          >
            <Users size={11} />
            <span>
              {supplierFilter.name || supplierFilter.nif || "—"}
              {supplierFilter.nif && supplierFilter.name && (
                <span className="opacity-60 ml-1 font-mono">· {supplierFilter.nif}</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setSupplierFilter(null)}
              className="hover:bg-white/10 rounded p-0.5 -mr-1"
              title={lang === "es" ? "Quitar filtro" : "Treure filtre"}
              data-testid="received-supplier-filter-clear"
            >
              <X size={11} />
            </button>
          </span>
        )}
        <button
          type="button"
          onClick={async () => {
            // Count anomalies in the current loaded list first so we can
            // offer a 1-click "delete all suspicious" shortcut.
            const anomalies = list.filter((r) => r.ocr_anomaly && r.status === "pending");
            if (anomalies.length > 0) {
              if (window.confirm(
                lang === "es"
                  ? `Eliminar las ${anomalies.length} facturas pendientes marcadas como sospechosas (importe atípico vs. histórico)?`
                  : `Eliminar les ${anomalies.length} factures pendents marcades com a sospitoses (import atípic vs. històric)?`
              )) {
                try {
                  const { data } = await api.post("/received-invoices/bulk-delete", {
                    filter_anomaly: true,
                    only_pending: true,
                  });
                  toast.success(
                    lang === "es"
                      ? `${data.deleted_count} facturas eliminadas`
                      : `${data.deleted_count} factures eliminades`
                  );
                  load();
                  return;
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Error");
                  return;
                }
              }
            }
            // Fallback: prompt for an exact total to delete.
            const raw = window.prompt(
              lang === "es"
                ? "Importe exacto a eliminar (€). Solo borrará facturas PENDIENTES con ese total. Ej: 7189"
                : "Import exacte a eliminar (€). Només esborra factures PENDENTS amb aquest total. Ex: 7189"
            );
            if (!raw) return;
            const val = parseFloat(raw.replace(",", "."));
            if (Number.isNaN(val) || val <= 0) {
              toast.error(lang === "es" ? "Importe inválido" : "Import no vàlid");
              return;
            }
            if (!window.confirm(
              lang === "es"
                ? `Esto eliminará TODAS las facturas pendientes con total = ${val} €. ¿Continuar?`
                : `Això eliminarà TOTES les factures pendents amb total = ${val} €. Continuar?`
            )) return;
            try {
              const { data } = await api.post("/received-invoices/bulk-delete", {
                filter_total: val,
                only_pending: true,
              });
              toast.success(
                lang === "es"
                  ? `${data.deleted_count} facturas eliminadas`
                  : `${data.deleted_count} factures eliminades`
              );
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Error");
            }
          }}
          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium border bg-rose-500/10 border-rose-500/30 text-rose-200 hover:bg-rose-500/20"
          data-testid="received-cleanup-btn"
          title={lang === "es"
            ? "Elimina sospechosas o por importe OCR erróneo"
            : "Esborra sospitoses o per import OCR erroni"}
        >
          <Trash2 size={12} className="inline -mt-0.5 mr-1" />
          {lang === "es" ? "Limpiar errores OCR" : "Netejar errors OCR"}
          {list.filter((r) => r.ocr_anomaly && r.status === "pending").length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-amber-500 text-amber-950 text-[10px] font-bold leading-none">
              {list.filter((r) => r.ocr_anomaly && r.status === "pending").length}
            </span>
          )}
        </button>
      </div>

      {/* List */}
      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
            {t("common.loading")}
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p className="text-sm">
              {lang === "es"
                ? "Sin facturas. Sube tu primera factura recibida."
                : "Sense factures. Puja la teva primera factura rebuda."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-xs uppercase tracking-wider text-slate-400">
                  <th className="text-left p-3">{lang === "es" ? "Fecha" : "Data"}</th>
                  <th className="text-left p-3">{lang === "es" ? "Proveedor" : "Proveïdor"}</th>
                  <th className="text-left p-3">Nº</th>
                  <th className="text-left p-3 hidden md:table-cell">{lang === "es" ? "Categoría" : "Categoria"}</th>
                  <th className="text-right p-3">Base</th>
                  <th className="text-right p-3">IVA</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-center p-3 hidden sm:table-cell">{lang === "es" ? "Estado" : "Estat"}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}
                      className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setEditing(r)}
                      data-testid={`received-row-${r.id}`}>
                    <td className="p-3 text-slate-300">{r.issue_date || "—"}</td>
                    <td className="p-3">
                      <div className="text-slate-200 font-medium truncate max-w-[200px]">{r.supplier_name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.supplier_nif}</div>
                    </td>
                    <td className="p-3 text-slate-300 font-mono text-xs">{r.document_number || "—"}</td>
                    <td className="p-3 hidden md:table-cell text-slate-300 text-xs">{r.category || "—"}</td>
                    <td className="p-3 text-right text-slate-300 font-mono">{fmt(r.subtotal)}</td>
                    <td className="p-3 text-right text-slate-300 font-mono">{fmt(r.vat_amount)}</td>
                    <td className="p-3 text-right text-white font-mono font-semibold">
                      <div className="flex items-center justify-end gap-1.5">
                        {r.ocr_anomaly && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            title={r.ocr_anomaly_reason || (lang === "es"
                              ? "Importe sospechoso vs. histórico del proveedor"
                              : "Import sospitós vs. historial del proveïdor")}
                            data-testid={`received-anomaly-${r.id}`}
                          >
                            <AlertTriangle size={10} />
                            {lang === "es" ? "Revisa" : "Revisa"}
                          </span>
                        )}
                        <span>{fmt(r.total)}</span>
                      </div>
                    </td>
                    <td className="p-3 hidden sm:table-cell text-center">
                      <StatusPill status={r.status} lang={lang} />
                    </td>
                    <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleDelete(r.id, r.supplier_name)}
                              className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"
                              data-testid={`received-delete-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}

      {/* Edit dialog */}
      {editing && (
        <EditDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* Scan preview dialog (CamScanner) — supports multi-page capture */}
      {scanPages && scanPages.length > 0 && (
        <ScanPreviewDialog
          pages={scanPages}
          busy={uploading}
          onConfirm={(mode) => runScanUpload(mode)}
          onAddPage={addPage}
          onRemovePage={removePage}
          onReorderPages={reorderPages}
          onRotatePage={rotatePage}
          onCancel={() => {
            setScanPages(null);
            if (inputRef.current) inputRef.current.value = "";
            if (cameraRef.current) cameraRef.current.value = "";
          }}
        />
      )}

      {/* Duplicate confirmation dialog */}
      {duplicatePrompt && (
        <DuplicateDialog
          existing={duplicatePrompt.existing}
          reason={duplicatePrompt.reason}
          busy={uploading}
          onCancel={() => {
            setDuplicatePrompt(null);
            setScanPages(null);
            if (inputRef.current) inputRef.current.value = "";
            if (cameraRef.current) cameraRef.current.value = "";
          }}
          onOpenExisting={() => {
            const id = duplicatePrompt.existing.id;
            setDuplicatePrompt(null);
            setScanPages(null);
            const found = list.find((it) => it.id === id);
            if (found) setEditing(found);
            else toast.info(lang === "es" ? "Buscando original..." : "Cercant l'original...");
          }}
          onForce={() => {
            const { file, pages, mode } = duplicatePrompt;
            setDuplicatePrompt(null);
            if (pages && pages.length > 0) {
              runScanUpload(mode, true, pages);
            } else if (file) {
              runUpload(file, mode, true);
            }
          }}
        />
      )}

      {/* Bulk upload dialog */}
      {bulkQueue && (
        <BulkUploadDialog
          files={bulkQueue}
          onDone={() => {
            setBulkQueue(null);
            if (bulkRef.current) bulkRef.current.value = "";
            load();
          }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, accent = "blue" }) {
  const colors = {
    blue: "from-blue-500/10 to-blue-600/5 border-blue-500/20 text-blue-300",
    emerald: "from-emerald-500/10 to-emerald-600/5 border-emerald-500/20 text-emerald-300",
    amber: "from-amber-500/10 to-amber-600/5 border-amber-500/20 text-amber-300",
  }[accent];
  return (
    <div className={`card-surface bg-gradient-to-br ${colors} border p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      <div className="text-xl font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}

function StatusPill({ status, lang }) {
  const map = {
    pending: { l: lang === "es" ? "Pendiente" : "Pendent", c: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
    validated: { l: lang === "es" ? "Validada" : "Validada", c: "bg-blue-500/10 border-blue-500/30 text-blue-300" },
    paid: { l: lang === "es" ? "Pagada" : "Pagada", c: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
    rejected: { l: lang === "es" ? "Rechazada" : "Rebutjada", c: "bg-rose-500/10 border-rose-500/30 text-rose-300" },
  }[status] || { l: status, c: "bg-slate-500/10 border-slate-500/30 text-slate-300" };
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${map.c}`}>{map.l}</span>;
}

function EditDialog({ item, onClose, onSaved }) {
  const { lang } = useI18n();
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(item?.classification || null);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/received-invoices/${item.id}/classify`);
      const s = r.data || {};
      setSuggestion(s);
      // Apply suggestion only to empty fields (don't overwrite user edits)
      setForm((f) => ({
        ...f,
        pgc_account: f.pgc_account || s.pgc_account || "",
        category: f.category || s.category || "",
      }));
      toast.success(lang === "es"
        ? `Sugerencia: ${s.category} · cuenta ${s.pgc_account}`
        : `Suggeriment: ${s.category} · compte ${s.pgc_account}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((f) => ({
      ...f,
      pgc_account: suggestion.pgc_account || f.pgc_account,
      category: suggestion.category || f.category,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        supplier_name: form.supplier_name || "",
        supplier_nif: form.supplier_nif || "",
        supplier_address: form.supplier_address || "",
        supplier_email: form.supplier_email || "",
        supplier_phone: form.supplier_phone || "",
        document_number: form.document_number || "",
        issue_date: form.issue_date || null,
        operation_date: form.operation_date || null,
        due_date: form.due_date || null,
        payment_method: form.payment_method || "",
        subtotal: Number(form.subtotal) || 0,
        vat_amount: Number(form.vat_amount) || 0,
        irpf_amount: Number(form.irpf_amount) || 0,
        total: Number(form.total) || 0,
        currency: form.currency || "EUR",
        items: form.items || [],
        taxes: form.taxes || [],
        other_charges: Number(form.other_charges) || 0,
        category: form.category || "",
        pgc_account: form.pgc_account || "",
        notes: form.notes || "",
        status: form.status || "pending",
      };
      await api.put(`/received-invoices/${item.id}`, payload);
      toast.success(lang === "es" ? "Guardado" : "Desat");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
         onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#0B1220] border border-white/10 rounded-2xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="received-edit-dialog">
        <div className="sticky top-0 z-10 bg-[#0B1220] border-b border-white/10 p-5 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-white">
              {lang === "es" ? "Editar factura recibida" : "Edita factura rebuda"}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-400">{form.supplier_name || "—"}</span>
              <span className="text-xs text-slate-500">·</span>
              <span className="text-xs text-slate-400">{form.document_number || "—"}</span>
              {form.confidence > 0 && (
                <ConfidenceBadge confidence={form.confidence} />
              )}
              {(item.ocr_engine === "tesseract" || form.ocr_engine === "tesseract") && (
                <span className="px-1.5 py-0.5 rounded text-[10px] border bg-amber-500/10 text-amber-300 border-amber-500/30"
                      title={item.ocr_cloud_error || ""}
                      data-testid="ri-tesseract-badge">
                  {lang === "es" ? "OCR local" : "OCR local"}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${API}/received-invoices/${item.id}/file?token=${encodeURIComponent(localStorage.getItem("hosys_token") || "")}`}
               target="_blank" rel="noopener noreferrer"
               className="px-3 py-1.5 rounded-lg text-xs border border-white/10 text-slate-300 hover:bg-white/5 flex items-center gap-1.5">
              <ExternalLink size={12} /> {lang === "es" ? "Ver original" : "Veure original"}
            </a>
            <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-5 grid lg:grid-cols-2 gap-5">
          {/* Supplier */}
          <Section title={lang === "es" ? "Proveedor" : "Proveïdor"}>
            <FormField label={lang === "es" ? "Nombre" : "Nom"}>
              <Input value={form.supplier_name || ""} onChange={(e) => set("supplier_name", e.target.value)} className="bg-white/5 border-white/10" data-testid="ri-supplier-name" />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="NIF/CIF">
                <Input value={form.supplier_nif || ""} onChange={(e) => set("supplier_nif", e.target.value)} className="bg-white/5 border-white/10" data-testid="ri-supplier-nif" />
              </FormField>
              <FormField label={lang === "es" ? "Teléfono" : "Telèfon"}>
                <Input value={form.supplier_phone || ""} onChange={(e) => set("supplier_phone", e.target.value)} className="bg-white/5 border-white/10" />
              </FormField>
            </div>
            <FormField label={lang === "es" ? "Dirección" : "Adreça"}>
              <Input value={form.supplier_address || ""} onChange={(e) => set("supplier_address", e.target.value)} className="bg-white/5 border-white/10" />
            </FormField>
          </Section>

          {/* Invoice */}
          <Section title={lang === "es" ? "Datos factura" : "Dades factura"}>
            <div className="grid grid-cols-2 gap-3">
              <FormField label={lang === "es" ? "Nº factura" : "Nº factura"}>
                <Input value={form.document_number || ""} onChange={(e) => set("document_number", e.target.value)} className="bg-white/5 border-white/10" data-testid="ri-doc-number" />
              </FormField>
              <FormField label={lang === "es" ? "Fecha emisión" : "Data emissió"}>
                <Input type="date" value={form.issue_date || ""} onChange={(e) => set("issue_date", e.target.value)} className="bg-white/5 border-white/10" data-testid="ri-issue-date" />
              </FormField>
              <FormField label={lang === "es" ? "Vencimiento" : "Venciment"}>
                <Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="bg-white/5 border-white/10" />
              </FormField>
              <FormField label={lang === "es" ? "Forma de pago" : "Forma de pagament"}>
                <Input value={form.payment_method || ""} onChange={(e) => set("payment_method", e.target.value)} className="bg-white/5 border-white/10" />
              </FormField>
            </div>
          </Section>

          {/* Amounts */}
          <Section title={lang === "es" ? "Importes" : "Imports"}>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Base imposable">
                <Input type="number" step="0.01" value={form.subtotal || 0} onChange={(e) => set("subtotal", e.target.value)} className="bg-white/5 border-white/10 font-mono" data-testid="ri-subtotal" />
              </FormField>
              <FormField label="IVA">
                <Input type="number" step="0.01" value={form.vat_amount || 0} onChange={(e) => set("vat_amount", e.target.value)} className="bg-white/5 border-white/10 font-mono" data-testid="ri-vat" />
              </FormField>
              <FormField label="IRPF">
                <Input type="number" step="0.01" value={form.irpf_amount || 0} onChange={(e) => set("irpf_amount", e.target.value)} className="bg-white/5 border-white/10 font-mono" />
              </FormField>
              <FormField label="Total">
                <Input type="number" step="0.01" value={form.total || 0} onChange={(e) => set("total", e.target.value)} className="bg-white/5 border-white/10 font-mono font-bold" data-testid="ri-total" />
              </FormField>
            </div>
          </Section>

          {/* Multi-tax editor — IVA múltiple + IEH + ECO + sucre + IRPF + recàrrec */}
          <Section title={lang === "es" ? "Impuestos detallados" : "Impostos detallats"}>
            <TaxesEditor
              value={form.taxes || []}
              onChange={(t) => {
                // Auto-update legacy vat_amount/irpf_amount totals when the
                // user edits the detailed taxes[] so the summary card stays
                // in sync.
                const next = { ...form, taxes: t };
                const ivaSum = t
                  .filter((x) => !x.is_retention && (x.code || "").startsWith("IVA"))
                  .reduce((s, x) => s + (Number(x.amount) || 0), 0);
                const irpfSum = t
                  .filter((x) => x.code === "IRPF")
                  .reduce((s, x) => s + (Number(x.amount) || 0), 0);
                next.vat_amount = Number(ivaSum.toFixed(2));
                next.irpf_amount = Number(irpfSum.toFixed(2));
                setForm(next);
              }}
              subtotal={form.subtotal}
              otherCharges={form.other_charges}
            />
          </Section>

          {/* Accounting */}
          <Section title={lang === "es" ? "Contabilidad" : "Comptabilitat"}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {lang === "es" ? "Clasificación automática" : "Classificació automàtica"}
              </div>
              <button
                type="button"
                onClick={suggest}
                disabled={suggesting}
                className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="ri-classify-btn"
              >
                {suggesting ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Sparkles size={11} />
                )}
                {lang === "es" ? "Sugerir con IA" : "Suggerir amb IA"}
              </button>
            </div>

            {suggestion && (
              <SuggestionBadge
                suggestion={suggestion}
                lang={lang}
                onApply={applySuggestion}
                applied={
                  form.pgc_account === suggestion.pgc_account &&
                  form.category === suggestion.category
                }
              />
            )}

            <FormField label={lang === "es" ? "Cuenta PGC" : "Compte PGC"}>
              <Select value={form.pgc_account || ""} onValueChange={(v) => set("pgc_account", v)}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="ri-pgc">
                  <SelectValue placeholder={lang === "es" ? "Selecciona cuenta" : "Selecciona compte"} />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {PGC_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={lang === "es" ? "Categoría" : "Categoria"}>
              <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="ri-category">
                  <SelectValue placeholder={lang === "es" ? "Selecciona categoría" : "Selecciona categoria"} />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={lang === "es" ? "Estado" : "Estat"}>
              <div className="flex flex-wrap gap-1.5">
                {["pending", "validated", "paid", "rejected"].map((s) => {
                  const active = form.status === s;
                  return (
                    <button key={s} type="button" onClick={() => set("status", s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                              active ? "bg-blue-600 border-blue-500 text-white"
                              : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"}`}
                            data-testid={`ri-status-${s}`}>
                      {s === "pending" ? (lang === "es" ? "Pendiente" : "Pendent")
                        : s === "validated" ? (lang === "es" ? "Validada" : "Validada")
                        : s === "paid" ? (lang === "es" ? "Pagada" : "Pagada")
                        : (lang === "es" ? "Rechazada" : "Rebutjada")}
                    </button>
                  );
                })}
              </div>
            </FormField>
            <FormField label={lang === "es" ? "Notas" : "Notes"}>
              <Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-white/5 border-white/10 resize-none" />
            </FormField>
          </Section>
        </div>

        <div className="sticky bottom-0 z-10 bg-[#0B1220] border-t border-white/10 p-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-slate-400 hover:text-white">
            {lang === "es" ? "Cancelar" : "Cancel·lar"}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ri-save-btn">
            {saving ? (<><Loader2 size={14} className="mr-2 animate-spin" />{lang === "es" ? "Guardando..." : "Desant..."}</>)
              : (<><CheckCircle2 size={14} className="mr-2" />{lang === "es" ? "Guardar" : "Desa"}</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function BulkUploadDialog({ files, onDone }) {
  const { lang } = useI18n();
  const fmt = (n) => new Intl.NumberFormat("ca-ES", {
    style: "currency", currency: "EUR",
  }).format(Number(n) || 0);
  const [combine, setCombine] = React.useState(false);  // false = N invoices, true = 1 multi-page invoice
  const [queue, setQueue] = React.useState(
    files.map((f) => ({
      file: f,
      status: "pending",       // pending → uploading → done | error | duplicate
      message: "",
      result: null,
    }))
  );
  const [done, setDone] = React.useState(false);
  const [started, setStarted] = React.useState(false);

  // Are all files images (eligible for multi-page combine)?
  const allImages = React.useMemo(
    () => files.every((f) => f.type && f.type.startsWith("image/")),
    [files]
  );
  const canCombine = allImages && files.length >= 2;

  const start = async () => {
    setStarted(true);
    if (combine) {
      // Single multi-page invoice → call /upload-multipage with all files at once
      setQueue((q) => q.map((it) => ({ ...it, status: "uploading" })));
      try {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        fd.append("mode", "auto");
        const r = await api.post("/received-invoices/upload-multipage", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
        const data = r.data;
        setQueue((q) => q.map((it) => ({
          ...it, status: "done", result: data,
          message: data.duplicate_warning
            ? (lang === "es" ? "Posible duplicado" : "Possible duplicat") : "",
        })));
      } catch (e) {
        const code = e?.response?.data?.detail?.code;
        const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message || "Error";
        const status = e?.response?.status === 409 && (code === "duplicate_file" || code === "duplicate_invoice")
          ? "duplicate" : "error";
        setQueue((q) => q.map((it) => ({
          ...it, status,
          message: typeof msg === "string" ? msg : "Error",
        })));
      } finally {
        setDone(true);
      }
      return;
    }
    // Separate invoices → process sequentially (original flow)
    for (let i = 0; i < files.length; i++) {
      setQueue((q) => q.map((it, idx) => idx === i ? { ...it, status: "uploading" } : it));
      try {
        const fd = new FormData();
        fd.append("file", files[i]);
        fd.append("mode", "auto");  // CamScanner-like crop + deskew + grayscale + CLAHE
        const r = await api.post("/received-invoices/upload?force=false", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        setQueue((q) => q.map((it, idx) => idx === i ? {
          ...it, status: "done", result: r.data,
          message: r.data.duplicate_warning
            ? (lang === "es" ? "Posible duplicado" : "Possible duplicat")
            : "",
        } : it));
      } catch (e) {
        const code = e?.response?.data?.detail?.code;
        if (e?.response?.status === 409 && (code === "duplicate_file" || code === "duplicate_invoice")) {
          setQueue((q) => q.map((it, idx) => idx === i ? {
            ...it, status: "duplicate",
            message: code === "duplicate_invoice"
              ? (lang === "es" ? "Misma factura ya contabilizada" : "Mateixa factura ja comptabilitzada")
              : (lang === "es" ? "Ya estaba subida" : "Ja estava pujada"),
          } : it));
        } else {
          const msg = e?.response?.data?.detail || e.message || "Error";
          setQueue((q) => q.map((it, idx) => idx === i ? {
            ...it, status: "error",
            message: typeof msg === "string" ? msg : (msg.message || "Error"),
          } : it));
        }
      }
    }
    setDone(true);
  };

  const stats = React.useMemo(() => ({
    done: queue.filter((q) => q.status === "done").length,
    duplicate: queue.filter((q) => q.status === "duplicate").length,
    error: queue.filter((q) => q.status === "error").length,
    pending: queue.filter((q) => q.status === "pending" || q.status === "uploading").length,
  }), [queue]);

  return (
    <div className="fixed inset-0 z-[115] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
         data-testid="bulk-upload-overlay">
      <div className="w-full max-w-2xl max-h-[90vh] bg-[#0B1220] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
           data-testid="bulk-upload-dialog">
        <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Files className="w-5 h-5 text-blue-300" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-white">
                {combine
                  ? (lang === "es" ? "Factura multi-página" : "Factura multi-pàgina")
                  : (lang === "es" ? "Subida por lote" : "Pujada en lot")}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {!started
                  ? (lang === "es"
                     ? `${files.length} ficheros seleccionados`
                     : `${files.length} fitxers seleccionats`)
                  : done
                  ? (combine
                     ? (lang === "es" ? "Procesado" : "Processat")
                     : (lang === "es"
                        ? `${stats.done} OK · ${stats.duplicate} duplicadas · ${stats.error} errores`
                        : `${stats.done} OK · ${stats.duplicate} duplicades · ${stats.error} errors`))
                  : (lang === "es"
                     ? `Procesando ${queue.length - stats.pending} de ${queue.length}...`
                     : `Processant ${queue.length - stats.pending} de ${queue.length}...`)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onDone}
            disabled={started && !done}
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400 disabled:opacity-30"
            data-testid="bulk-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Combine toggle — only before start */}
        {canCombine && !started && (
          <div className="px-5 py-3 border-b border-white/10 bg-blue-500/5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={combine}
                onChange={(e) => setCombine(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 accent-blue-500"
                data-testid="bulk-combine-checkbox"
              />
              <div className="text-sm">
                <div className="font-medium text-white">
                  {lang === "es"
                    ? "Estos ficheros son una única factura multi-página"
                    : "Aquests fitxers són una sola factura multi-pàgina"}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {lang === "es"
                    ? "Las páginas se combinarán y se procesarán como un único documento."
                    : "Les pàgines es combinaran i es processaran com un sol document."}
                </div>
              </div>
            </label>
          </div>
        )}

        <div className="overflow-y-auto flex-1 divide-y divide-white/5">
          {queue.map((it, idx) => (
            <div key={idx} className="px-5 py-3 flex items-center gap-3"
                 data-testid={`bulk-row-${idx}`}>
              <BulkStatusIcon status={it.status} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200 truncate">{it.file.name}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                  <span>{(it.file.size / 1024).toFixed(0)} KB</span>
                  {it.message && (
                    <span className={
                      it.status === "error" ? "text-rose-300" :
                      it.status === "duplicate" ? "text-amber-300" : "text-slate-400"
                    }>{it.message}</span>
                  )}
                  {it.result?.supplier_name && (
                    <span className="text-slate-300 truncate">
                      → {it.result.supplier_name}
                      {it.result.total > 0 ? ` · ${fmt(it.result.total)}` : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10 bg-black/20 flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs">
            <StatBadge color="emerald" count={stats.done} label={lang === "es" ? "OK" : "OK"} />
            <StatBadge color="amber" count={stats.duplicate} label={lang === "es" ? "Dupl." : "Dupl."} />
            <StatBadge color="rose" count={stats.error} label={lang === "es" ? "Err." : "Err."} />
            <StatBadge color="slate" count={stats.pending} label={lang === "es" ? "Pend." : "Pend."} />
          </div>
          {!started ? (
            <Button
              onClick={start}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="bulk-start-btn"
            >
              {combine
                ? (lang === "es"
                   ? `Procesar ${files.length} páginas`
                   : `Processa ${files.length} pàgines`)
                : (lang === "es"
                   ? `Procesar ${files.length} ficheros`
                   : `Processa ${files.length} fitxers`)}
            </Button>
          ) : (
            <Button
              onClick={onDone}
              disabled={!done}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40"
              data-testid="bulk-finish-btn"
            >
              {done
                ? (lang === "es" ? "Cerrar" : "Tanca")
                : (<><Loader2 size={14} className="animate-spin mr-2" />
                    {lang === "es" ? "Procesando..." : "Processant..."}</>)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkStatusIcon({ status }) {
  if (status === "done") return <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />;
  if (status === "duplicate") return <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />;
  if (status === "error") return <X size={16} className="text-rose-400 flex-shrink-0" />;
  if (status === "uploading") return <Loader2 size={16} className="text-blue-400 animate-spin flex-shrink-0" />;
  return <div className="w-4 h-4 rounded-full border border-white/20 flex-shrink-0" />;
}

function StatBadge({ color, count, label }) {
  const cls = {
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    rose: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    slate: "bg-white/5 text-slate-400 border-white/10",
  }[color];
  return (
    <span className={`px-2 py-0.5 rounded border ${cls} font-mono`}>
      {count} {label}
    </span>
  );
}

function DuplicateDialog({ existing, busy, reason, onCancel, onOpenExisting, onForce }) {
  const { lang } = useI18n();
  const fmt = (n) => new Intl.NumberFormat("ca-ES", {
    style: "currency", currency: "EUR",
  }).format(Number(n) || 0);
  const isInvoiceMatch = reason === "invoice";
  const desc = isInvoiceMatch
    ? (lang === "es"
        ? "Esta factura (mismo proveedor + número) ya está contabilizada. No la registres dos veces."
        : "Aquesta factura (mateix proveïdor + número) ja està comptabilitzada. No la registris dos cops.")
    : (lang === "es"
        ? "Este fichero ya se había subido anteriormente. Aquí está la factura existente:"
        : "Aquest fitxer ja s'havia pujat anteriorment. Aquí tens la factura existent:");
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-150"
         onClick={busy ? undefined : onCancel}
         data-testid="duplicate-overlay">
      <div className="w-full max-w-md bg-[#0B1220] border border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}
           data-testid="duplicate-dialog">
        <div className="p-5 border-b border-white/10 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-white">
              {lang === "es" ? "Factura duplicada" : "Factura duplicada"}
            </h3>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              {desc}
            </p>
          </div>
        </div>
        <div className="p-5 space-y-2 bg-black/20">
          <Row k={lang === "es" ? "Proveedor" : "Proveïdor"}
               v={existing?.supplier_name || "—"} />
          <Row k={lang === "es" ? "Nº documento" : "Núm. document"}
               v={existing?.document_number || "—"} />
          <Row k={lang === "es" ? "Fecha" : "Data"}
               v={existing?.issue_date || "—"} />
          <Row k="Total" v={fmt(existing?.total)} mono />
          <Row k={lang === "es" ? "Estado" : "Estat"}
               v={existing?.status || "pending"} />
        </div>
        <div className="p-5 flex flex-col sm:flex-row gap-2 border-t border-white/10">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 border-white/10 text-slate-300 hover:bg-white/5"
            data-testid="duplicate-cancel-btn"
          >
            {lang === "es" ? "Cancelar" : "Cancel·la"}
          </Button>
          <Button
            variant="outline"
            onClick={onOpenExisting}
            disabled={busy}
            className="flex-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
            data-testid="duplicate-open-existing-btn"
          >
            {lang === "es" ? "Abrir la existente" : "Obre l'existent"}
          </Button>
          <Button
            onClick={onForce}
            disabled={busy}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="duplicate-force-btn"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              lang === "es" ? "Subir igual" : "Puja igual"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{k}</span>
      <span className={`text-slate-100 ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}

function ConfidenceBadge({ confidence }) {
  const c = Number(confidence);
  const color = c >= 0.7 ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
    : c >= 0.4 ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
    : "bg-rose-500/10 text-rose-300 border-rose-500/30";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] border ${color}`}>
      OCR {Math.round(c * 100)}%
    </span>
  );
}

function SuggestionBadge({ suggestion, lang, onApply, applied }) {
  const c = Number(suggestion?.confidence || 0);
  const src = suggestion?.source;
  const srcLabel =
    src === "history"
      ? (lang === "es" ? "Histórico" : "Històric")
      : src === "ai"
      ? "IA"
      : src === "default"
      ? (lang === "es" ? "Por defecto" : "Per defecte")
      : src === "error"
      ? (lang === "es" ? "Error IA" : "Error IA")
      : "";
  const tone =
    src === "history"
      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
      : src === "ai"
      ? "border-indigo-500/30 bg-indigo-500/5 text-indigo-200"
      : "border-amber-500/30 bg-amber-500/5 text-amber-200";
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${tone}`}
         data-testid="ri-suggestion-banner">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 font-semibold flex-wrap">
          <Sparkles size={11} />
          <span>{lang === "es" ? "Sugerencia" : "Suggeriment"}</span>
          {srcLabel && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-black/20 text-[9px] uppercase tracking-wider">
              {srcLabel}
            </span>
          )}
          {c > 0 && (
            <span className="text-[10px] opacity-70">· {Math.round(c * 100)}%</span>
          )}
          {src === "history" && suggestion?.match_count > 0 && (
            <span
              className="ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-200"
              data-testid="ri-suggestion-learned-badge"
              title={
                lang === "es"
                  ? `${suggestion.match_count}/${suggestion.match_total || suggestion.match_count} clasificaciones idénticas (${suggestion.match_basis === "nif" ? "NIF" : "nombre"})`
                  : `${suggestion.match_count}/${suggestion.match_total || suggestion.match_count} classificacions idèntiques (${suggestion.match_basis === "nif" ? "NIF" : "nom"})`
              }
            >
              {lang === "es"
                ? `Aprendido · ${suggestion.match_count}`
                : `Après · ${suggestion.match_count}`}
            </span>
          )}
        </div>
        {!applied && onApply && (
          <button
            type="button"
            onClick={onApply}
            className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/10 hover:bg-white/20 text-white"
            data-testid="ri-suggestion-apply-btn"
          >
            {lang === "es" ? "Aplicar" : "Aplica"}
          </button>
        )}
        {applied && (
          <span className="flex items-center gap-1 text-[10px] opacity-80">
            <CheckCircle2 size={11} />
            {lang === "es" ? "Aplicado" : "Aplicat"}
          </span>
        )}
      </div>
      <div className="leading-snug">
        <span className="font-mono">{suggestion?.pgc_account}</span>
        {" · "}
        <span>{suggestion?.category}</span>
        {suggestion?.reasoning ? (
          <div className="opacity-70 mt-0.5">{suggestion.reasoning}</div>
        ) : null}
      </div>
    </div>
  );
}
